import React, { useEffect, useMemo, useState } from "react";
import { store } from "../../redux/oldStore.tsx";
import { toast } from "react-toastify";
import NhdNav from "../Layout/NhdNav";

const LEVELS = [
  { value: 50, label: "50 — Contest access" },
  { value: 100, label: "100 — User management" },
  { value: 1000, label: "1000 — Admin" },
];

function displayName(u) {
  return [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email || "";
}

function siteLabel(s) {
  const state = s.stateName || s.state || "";
  const name = s.name || `Contest ${s.id}`;
  return state ? `${state} — ${name}` : name;
}

const emptyInvite = {
  email: "",
  firstName: "",
  lastName: "",
  securityLevel: 50,
  clientIds: [],
};

export default function Users() {
  const [ready, setReady] = useState(false);
  const [info, setInfo] = useState(null);
  const [search, setSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [adminSearch, setAdminSearch] = useState("");
  const [adminPersonId, setAdminPersonId] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editSearch, setEditSearch] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState(emptyInvite);
  const [inviteSearch, setInviteSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    store.server
      .getApi("Users/Info")
      .then((r) => {
        if (r && r.Success) {
          setInfo(r.Value);
        } else {
          toast.error((r && r.Message) || "Could not load users");
        }
        setReady(true);
      })
      .catch(() => {
        toast.error("Could not load users");
        setReady(true);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const user = info && info.user;
  const securityLevel = (info && info.securityLevel) || 0;
  const canInvite = !!(info && info.canInvite);
  const users = (info && info.users) || [];
  const pending = (info && info.pendingInvites) || [];
  const contests = (info && info.contests) || [];
  const adminCandidates = (info && info.adminCandidates) || [];

  const filteredUsers = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = [
        u.firstName,
        u.lastName,
        u.email,
        u.contestName,
        String(u.securityLevel || ""),
        ...(u.sites || []).map(siteLabel),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.indexOf(q) > -1;
    });
  }, [users, search]);

  const filteredPending = useMemo(() => {
    const q = (pendingSearch || "").trim().toLowerCase();
    if (!q) return pending;
    return pending.filter((p) => {
      const hay = [
        p.firstName,
        p.lastName,
        p.email,
        String(p.securityLevel || ""),
        p.tokenExpired ? "expired" : "pending",
        ...(p.sites || []).map(siteLabel),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.indexOf(q) > -1;
    });
  }, [pending, pendingSearch]);

  const filteredAdmins = useMemo(() => {
    const q = (adminSearch || "").trim().toLowerCase();
    if (!q) return adminCandidates;
    return adminCandidates.filter((a) => {
      const hay = [a.state, a.fairName, a.firstName, a.lastName, a.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.indexOf(q) > -1;
    });
  }, [adminCandidates, adminSearch]);

  const allowedLevels = LEVELS.filter((l) => l.value <= securityLevel);

  const post = (url, data, okMsg) => {
    setBusy(true);
    store.server
      .postApi(url, data)
      .then((r) => {
        setBusy(false);
        if (r && r.Success) {
          if (okMsg || r.Message) toast.success(okMsg || r.Message);
          load();
          return r;
        }
        toast.error((r && r.Message) || "Request failed");
        return null;
      })
      .catch(() => {
        setBusy(false);
        toast.error("Request failed");
      });
  };

  const toggleSite = (userId, siteId, add) => {
    store.server
      .postApi("Users/Update", { userId, siteId, add: add ? 1 : 0 })
      .then((r) => {
        if (r && r.Success) {
          setEditUser((prev) => {
            if (!prev || prev.id !== userId) return prev;
            const sites = add
              ? [...(prev.sites || []), contests.find((c) => c.id === siteId)].filter(Boolean)
              : (prev.sites || []).filter((s) => s.id !== siteId);
            return { ...prev, sites };
          });
          setInfo((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              users: (prev.users || []).map((u) => {
                if (u.id !== userId) return u;
                const sites = add
                  ? [...(u.sites || []), contests.find((c) => c.id === siteId)].filter(Boolean)
                  : (u.sites || []).filter((s) => s.id !== siteId);
                return { ...u, sites };
              }),
            };
          });
        } else {
          toast.error((r && r.Message) || "Could not update access");
        }
      });
  };

  const addSuperuser = () => {
    if (!adminPersonId) {
      toast.info("Select an admin first.");
      return;
    }
    post("Users/AddSuperuser", { personId: adminPersonId }, "Superuser added.");
  };

  const setPassword = () => {
    if (!editUser) return;
    if (!tempPassword || tempPassword.length < 8) {
      toast.info("Password must be at least 8 characters.");
      return;
    }
    store.server
      .postApi("Users/SetTempPassword", {
        userId: editUser.id,
        password: tempPassword,
      })
      .then((r) => {
        if (r && r.Success) {
          toast.success(r.Message || "Temporary password set.");
          setTempPassword("");
        } else {
          toast.error((r && r.Message) || "Could not set password.");
        }
      });
  };

  const sendInvite = () => {
    if (!invite.email || invite.email.indexOf("@") === -1) {
      toast.info("Enter a valid email.");
      return;
    }
    if (!invite.securityLevel) {
      toast.info("Select a security level.");
      return;
    }
    setBusy(true);
    store.server
      .postApi("Users/Invite", {
        email: invite.email,
        firstName: invite.firstName,
        lastName: invite.lastName,
        securityLevel: invite.securityLevel,
        clientIds: invite.clientIds,
      })
      .then((r) => {
        setBusy(false);
        if (r && r.Success) {
          toast.success(r.Message || "Invite sent.");
          setShowInvite(false);
          setInvite(emptyInvite);
          load();
        } else {
          toast.error((r && r.Message) || "Could not send invite.");
        }
      })
      .catch(() => {
        setBusy(false);
        toast.error("Could not send invite.");
      });
  };

  const resend = (p) => post("Users/ResendInvite", { inviteId: p.id });
  const revoke = (p) => {
    if (
      !window.confirm(
        `Revoke the invite for ${p.email}? The emailed link will stop working.`
      )
    ) {
      return;
    }
    post("Users/RevokeInvite", { inviteId: p.id }, "Invite revoked.");
  };

  const toggleInviteSite = (id) => {
    setInvite((prev) => {
      const has = prev.clientIds.indexOf(id) > -1;
      return {
        ...prev,
        clientIds: has
          ? prev.clientIds.filter((x) => x !== id)
          : prev.clientIds.concat(id),
      };
    });
  };

  const filteredContests = (q) => {
    const query = (q || "").trim().toLowerCase();
    if (!query) return contests;
    return contests.filter((c) => {
      const hay = [c.stateName, c.state, c.name, String(c.id)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.indexOf(query) > -1;
    });
  };

  if (!ready) {
    return (
      <div className="nhd-page">
        <NhdNav user={null} onAuthChange={load} />
        <div className="nhd-page-body">Loading users…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="nhd-page">
        <NhdNav user={null} onAuthChange={load} />
        <div className="nhd-page-body">
          <h1>User Management</h1>
          <p>Sign in as an admin to manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nhd-page users-page">
      <NhdNav user={user} onAuthChange={load} />
      <div className="nhd-page-body">
        <div className="contests-header flex-between wrap">
          <h1>User Management</h1>
          {canInvite && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setInvite({
                  ...emptyInvite,
                  securityLevel: allowedLevels[0] ? allowedLevels[0].value : 50,
                });
                setShowInvite(true);
              }}
            >
              <i className="fad fa-envelope"></i> Invite user
            </button>
          )}
        </div>

        <div className="contests-search">
          <label htmlFor="users-search">Search users</label>
          <input
            id="users-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, contest, or level"
          />
        </div>

        <table className="nhd-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Primary contest</th>
              <th className="text-right">Security level</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id}>
                <td>{displayName(u)}</td>
                <td>{u.email}</td>
                <td>{u.contestName}</td>
                <td className="text-right">
                  {u.securityLevel}{" "}
                  <button
                    type="button"
                    className="btn-xs btn-secondary"
                    onClick={() => {
                      setEditUser(u);
                      setEditSearch("");
                      setTempPassword("");
                    }}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={4}>No users match that search.</td>
              </tr>
            )}
          </tbody>
        </table>

        {canInvite && (
          <section className="users-pending">
            <h2>Pending invites</h2>
            <div className="contests-search">
              <label htmlFor="pending-search">Search invites</label>
              <input
                id="pending-search"
                type="search"
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                placeholder="Search pending invites"
              />
            </div>
            {filteredPending.length === 0 ? (
              <p className="users-empty">No pending invites.</p>
            ) : (
              <table className="nhd-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Level</th>
                    <th>Contests</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPending.map((p) => (
                    <tr key={p.id}>
                      <td>{displayName(p)}</td>
                      <td>
                        {p.email}
                        {p.tokenExpired && (
                          <span className="users-expired"> Link expired</span>
                        )}
                      </td>
                      <td>{p.securityLevel}</td>
                      <td>
                        {(p.sites || []).length
                          ? p.sites.map(siteLabel).join("; ")
                          : "None assigned"}
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          className="btn-xs btn-success"
                          disabled={busy}
                          onClick={() => resend(p)}
                        >
                          Resend
                        </button>{" "}
                        <button
                          type="button"
                          className="btn-xs btn-danger"
                          disabled={busy}
                          onClick={() => revoke(p)}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        <section className="users-add">
          <h2>Add from contest admin</h2>
          <p className="users-help">
            To add a new superuser they must first be registered in their contest
            with the Admin role. They will use that password and email to sign in
            here.
          </p>
          <div className="contests-search">
            <label htmlFor="admin-search">Filter admins</label>
            <input
              id="admin-search"
              type="search"
              value={adminSearch}
              onChange={(e) => setAdminSearch(e.target.value)}
              placeholder="Search by state, contest, or name"
            />
          </div>
          <div className="users-add-row">
            <select
              value={adminPersonId}
              onChange={(e) => setAdminPersonId(e.target.value)}
            >
              <option value="">Select an admin…</option>
              {filteredAdmins.map((a) => (
                <option key={a.personId} value={a.personId}>
                  {a.state} — {a.fairName} — {a.firstName} {a.lastName}
                </option>
              ))}
            </select>
            <button type="button" className="btn-primary" onClick={addSuperuser}>
              Add user
            </button>
          </div>
        </section>
      </div>

      {editUser && (
        <div className="nhd-modal-backdrop" onClick={() => setEditUser(null)}>
          <div
            className="nhd-modal nhd-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nhd-modal-header">
              <h3>Edit superuser</h3>
              <span className="close click" onClick={() => setEditUser(null)}>
                ×
              </span>
            </div>
            <div className="nhd-modal-body">
              <p>
                <strong>{displayName(editUser)}</strong>
                <br />
                {editUser.email}
                <br />
                Security level: {editUser.securityLevel}
                {editUser.hasGoogleLogin ? " · Google login linked" : ""}
              </p>
              {info.canSetTempPassword && (
                <div className="form-input">
                  <label htmlFor="temp-password">Temp password</label>
                  <div className="users-inline">
                    <input
                      id="temp-password"
                      type="text"
                      value={tempPassword}
                      onChange={(e) => setTempPassword(e.target.value)}
                      placeholder="temporary password"
                      autoComplete="off"
                    />
                    <button type="button" className="btn-secondary" onClick={setPassword}>
                      Set
                    </button>
                  </div>
                </div>
              )}
              <h4>Contests {displayName(editUser)} has access to</h4>
              <input
                type="search"
                value={editSearch}
                onChange={(e) => setEditSearch(e.target.value)}
                placeholder="Search by state and contest name"
              />
              <div className="users-site-list">
                {filteredContests(editSearch).map((c) => {
                  const has = (editUser.sites || []).some((s) => s.id === c.id);
                  return (
                    <div key={c.id} className="users-site-row">
                      <span>
                        {c.stateName || c.state} — {c.name}
                      </span>
                      {has ? (
                        <button
                          type="button"
                          className="btn-xs btn-danger"
                          onClick={() => toggleSite(editUser.id, c.id, false)}
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-xs btn-success"
                          onClick={() => toggleSite(editUser.id, c.id, true)}
                        >
                          Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <div className="nhd-modal-backdrop" onClick={() => setShowInvite(false)}>
          <div
            className="nhd-modal nhd-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nhd-modal-header">
              <h3>Invite user</h3>
              <span className="close click" onClick={() => setShowInvite(false)}>
                ×
              </span>
            </div>
            <div className="nhd-modal-body">
              <p>
                Choose their security level and contests. We will email an invite
                link that is good for 2 hours. If it expires within a week, opening
                it sends a new link.
              </p>
              <div className="form-input">
                <label htmlFor="inv-email">Email</label>
                <input
                  id="inv-email"
                  type="email"
                  value={invite.email}
                  onChange={(e) =>
                    setInvite({ ...invite, email: e.target.value })
                  }
                />
              </div>
              <div className="form-input">
                <label htmlFor="inv-first">First name</label>
                <input
                  id="inv-first"
                  value={invite.firstName}
                  onChange={(e) =>
                    setInvite({ ...invite, firstName: e.target.value })
                  }
                />
              </div>
              <div className="form-input">
                <label htmlFor="inv-last">Last name</label>
                <input
                  id="inv-last"
                  value={invite.lastName}
                  onChange={(e) =>
                    setInvite({ ...invite, lastName: e.target.value })
                  }
                />
              </div>
              <div className="form-input">
                <label htmlFor="inv-level">Security level</label>
                <select
                  id="inv-level"
                  value={invite.securityLevel}
                  onChange={(e) =>
                    setInvite({
                      ...invite,
                      securityLevel: parseInt(e.target.value, 10),
                    })
                  }
                >
                  {allowedLevels.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-input">
                <label>Contests they can access</label>
                <input
                  type="search"
                  value={inviteSearch}
                  onChange={(e) => setInviteSearch(e.target.value)}
                  placeholder="Search contests"
                />
                <div className="users-site-list">
                  {filteredContests(inviteSearch).map((c) => {
                    const on = invite.clientIds.indexOf(c.id) > -1;
                    return (
                      <label key={c.id} className="users-site-check">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleInviteSite(c.id)}
                        />
                        <span className="users-site-name">
                          {c.stateName || c.state} — {c.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="users-help">
                  {invite.clientIds.length} contest
                  {invite.clientIds.length === 1 ? "" : "s"} selected. Level{" "}
                  {invite.securityLevel}.
                </div>
              </div>
              <div className="nhd-modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowInvite(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busy}
                  onClick={sendInvite}
                >
                  {busy ? "Sending…" : "Send invite"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
